import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { UpdateReason } from 'APP/constants/scroll';
import Entities from 'STORE';
import { Quote } from 'STORE/Messages/Message/Quote/Quote';

interface IQuotedTextPresenterProps {
  quote?: Quote;
  parentMessageId: string;
}

export interface IQuotedTextTitle {
  text: string;
  senderName?: string;
}

interface IQuotedTextPresenter {
  quote: Quote | null;
  title: IQuotedTextTitle | null;
  isLoading: boolean;
  isTextExpanded: boolean;
  parentMessageId: string;
  isSameGroup: boolean;
  titleWithUser: IQuotedTextTitle;
  titleWithChat: IQuotedTextTitle;
  titleWithChatAndUser: IQuotedTextTitle;
  setQuote(quote: Quote | null): void;
  setIsLoading(isLoading: boolean): void;
  setIsTextExpanded(isTextExpanded: boolean): void;
  setParentMessageId(parentMessageId: string): void;
  checkAccessToGroup(): Promise<boolean>;
  checkAccessToMessage(): Promise<boolean>;
  onAccessDenied(message: string): void;
  onClick(): void;
  endQuote(): void;
}

export function useQuotedTextPresenter({
  quote,
  parentMessageId,
}: IQuotedTextPresenterProps): IQuotedTextPresenter {
  const { t } = useTranslation();
  const presenter = useLocalObservable<IQuotedTextPresenter>(() => ({
    quote: null,
    isLoading: false,
    isTextExpanded: false,
    parentMessageId: parentMessageId,

    get isSameGroup(): boolean {
      return Entities.GroupsStore.activeGroupId === presenter.quote?.groupId;
    },

    get titleWithUser(): IQuotedTextTitle {
      return { text: presenter.quote?.senderName || presenter.quote?.groupTitle || '' };
    },

    get titleWithChat(): IQuotedTextTitle {
      return { text: presenter.quote?.groupTitle || '' };
    },

    get titleWithChatAndUser(): IQuotedTextTitle {
      return {
        text: presenter.quote?.groupTitle || '',
        senderName: presenter.quote?.senderName || '',
      };
    },

    get title(): IQuotedTextTitle | null {
      if (!presenter.quote) {
        return null;
      }

      if (
        presenter.quote.source.user ||
        (presenter.quote.source.userChat && presenter.isSameGroup)
      ) {
        return presenter.titleWithUser;
      }

      if (presenter.quote.source.chat) {
        return presenter.titleWithChat;
      }

      if (presenter.quote.source.userChat) {
        return presenter.titleWithChatAndUser;
      }

      if (presenter.quote.source.unknown) {
        return {
          text: presenter.quote.source.unknown.title,
        };
      }

      return null;
    },

    setQuote(quote: Quote | null): void {
      presenter.quote = quote;
    },

    setIsLoading(isLoading: boolean): void {
      presenter.isLoading = isLoading;
    },

    setIsTextExpanded(isTextExpanded: boolean): void {
      presenter.isTextExpanded = isTextExpanded;
    },

    setParentMessageId(parentMessageId: string): void {
      presenter.parentMessageId = parentMessageId;
    },

    async checkAccessToMessage(): Promise<boolean> {
      if (!presenter.quote?.messageId || !presenter.quote?.groupId) {
        return false;
      }

      if (!presenter.quote.message) {
        presenter.setIsLoading(true);
        await Tasks.messaging.loadMessagesByIds(presenter.quote?.groupId, [
          presenter.quote?.messageId,
        ]);
        presenter.setIsLoading(false);
      }

      return !!presenter.quote?.message;
    },

    async checkAccessToGroup(): Promise<boolean> {
      if (!presenter.quote?.groupId) {
        return false;
      }
      if (presenter.isSameGroup) {
        return true;
      }

      if (!presenter.quote?.group) {
        presenter.setIsLoading(true);
        await Tasks.group.loadGroupsByIds([presenter.quote.groupId]);
        presenter.setIsLoading(false);
      }

      const isPrivateGroup = presenter.quote?.group?.isClosed || presenter.quote?.group?.isFake;
      const isFakeMessage = Entities.GroupsStore.activeGroup?.messagesStore.getMessageById(
        presenter.parentMessageId
      )?.isFake;

      return !(isPrivateGroup && (presenter.quote?.group?.withoutMe || isFakeMessage));
    },

    onAccessDenied(message: string): void {
      Entities.toast.show(message);
      presenter.setIsTextExpanded(true);
      Entities.ChatStore.setScrollChanged(UpdateReason.FocusMessage, {
        isAnimated: true,
        messageId: presenter.parentMessageId,
      });
    },

    async onClick(): Promise<void> {
      if (!presenter.quote || presenter.isLoading) {
        return;
      }

      const hasAccessToGroup = await presenter.checkAccessToGroup();
      if (!hasAccessToGroup) {
        return presenter.onAccessDenied(t('chat_quote_private_message'));
      }

      const hasAccessToMessage = await presenter.checkAccessToMessage();
      if (!hasAccessToMessage) {
        return presenter.onAccessDenied(t('chat_quote_deleted_message'));
      }

      Tasks.messaging.highlightMessageText({
        groupId: presenter.quote.groupId || '',
        messageId: presenter.quote.messageId || '',
        text: presenter.quote!.text,
      });
    },

    endQuote(): void {
      Tasks.messaging.endQuote(null);
    },
  }));

  useEffect(() => {
    presenter.setQuote(quote || null);
  }, [quote]);

  useEffect(() => {
    presenter.setParentMessageId(parentMessageId);
  }, [parentMessageId]);

  return presenter;
}
